import { differenceInCalendarDays, format } from 'date-fns';
import { COMMUNITY_BASE } from '@/app/community/seo/urls';

/**
 * SB-1912 — Community notifications in the notification panel.
 *
 * The backend sends one `community` category with the kind in `type`, the
 * person who triggered it in `actor`, and the post and reply in `navigation`.
 * Comment and reply notifications collapse while unread, so `actorCount` can
 * be more than one.
 */

export const COMMUNITY_CATEGORY = 'community';

/** In the All tab, community items past this many fold into one bundle card. */
export const COMMUNITY_ITEMS_BEFORE_BUNDLE = 3;

export type CommunityNotificationType =
  | 'community_post_comment'
  | 'community_comment_reply'
  | 'community_mention';

export interface NotificationActor {
  userId?: string;
  full_name?: string;
  profile_image_url?: string;
}

export interface NotificationNavigation {
  type: string;
  entityId: string;
  params?: Record<string, unknown>;
}

/** The fields this file reads; the panel's own item type is a superset. */
export interface CommunityNotificationLike {
  title: string;
  category?: string;
  type?: string;
  actor?: NotificationActor;
  actorCount?: number;
  navigation?: NotificationNavigation[];
}

export const isCommunityNotification = (n: { category?: string }) =>
  n.category === COMMUNITY_CATEGORY;

const actorName = (n: CommunityNotificationLike) =>
  n.actor?.full_name?.trim() || 'Someone';

/**
 * "Sarah Ahmed mentioned you", "Sarah Ahmed and 2 others commented on your
 * post". The backend's own `title` is generic ("New comment on your post"),
 * so the card leads with the person, as the design does. Unknown types keep
 * the backend title.
 */
export const getCommunityHeadline = (n: CommunityNotificationLike): string => {
  const others = Math.max((n.actorCount ?? 1) - 1, 0);
  const who =
    others > 0
      ? `${actorName(n)} and ${others} ${others === 1 ? 'other' : 'others'}`
      : actorName(n);

  switch (n.type as CommunityNotificationType) {
    case 'community_mention':
      return `${actorName(n)} mentioned you`;
    case 'community_post_comment':
      return `${who} commented on your post`;
    case 'community_comment_reply':
      return `${who} replied to your comment`;
    default:
      return n.title;
  }
};

/**
 * Opens the post at its replies. The post page loads by ID and corrects the
 * category and slug itself (keeping the `#replies` anchor), so a placeholder
 * category and slug are enough here.
 */
export const getCommunityPostPath = (
  n: CommunityNotificationLike
): string | null => {
  const postId = n.navigation?.find(
    (nav) => nav.type === 'community_post'
  )?.entityId;
  return postId ? `${COMMUNITY_BASE}/general/${postId}/post#replies` : null;
};

/** "Sarah Ahmed" → "SA"; one name → its first two letters. */
export const getInitials = (name?: string): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** "Just now", "18 min ago", "3 hours ago", "Yesterday", "4 days ago", "02 Oct 2026". */
export const formatNotificationTime = (
  iso: string,
  now: Date = new Date()
): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;

  const days = differenceInCalendarDays(now, date);
  if (days === 0) {
    const hours = Math.floor(minutes / 60);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return format(date, 'dd MMM yyyy');
};
